import React from 'react';
import { 
  Paper, 
  Typography, 
  Link, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip
} from '@mui/material';
import { format, isAfter, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { UtilityService } from '../../../types/utility.types';

interface ContractRenewalTimelineProps {
  services: UtilityService[];
}

const ContractRenewalTimeline: React.FC<ContractRenewalTimelineProps> = ({ services = [] }) => {
  const navigate = useNavigate();
  
  const renewals = services
    .filter(service => service.contractEndDate)
    .map(service => {
      const endDate = service.contractEndDate ? new Date(service.contractEndDate) : null;
      const isOverdue = endDate ? isAfter(new Date(), endDate) : false;
      const daysUntilExpiry = endDate ? differenceInDays(endDate, new Date()) : null;
      
      return {
        ...service,
        endDate,
        isOverdue,
        daysUntilExpiry
      };
    })
    .sort((a, b) => {
      if (!a.endDate || !b.endDate) return 0;
      return a.endDate.getTime() - b.endDate.getTime();
    });

  const handleClientClick = (clientId: string) => {
    navigate(`/utilities/clients/${clientId}`);
  };

  const getUrgencyColor = (daysUntilExpiry: number | null, isOverdue: boolean) => {
    if (isOverdue) return 'error';
    if (daysUntilExpiry === null) return 'default';
    if (daysUntilExpiry <= 30) return 'error';
    if (daysUntilExpiry <= 60) return 'warning';
    return 'success';
  };

  const getUrgencyLabel = (daysUntilExpiry: number | null, isOverdue: boolean) => {
    if (isOverdue) return 'OVERDUE';
    if (daysUntilExpiry === null) return 'Unknown';
    if (daysUntilExpiry <= 30) return 'CRITICAL';
    if (daysUntilExpiry <= 60) return 'WARNING';
    return 'OK';
  };

  if (renewals.length === 0) {
    return (
      <Paper sx={{ 
        p: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        color: 'white'
      }}>
        <Typography sx={{ color: 'white' }}>No upcoming contract renewals</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ 
      p: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 2,
      color: 'white'
    }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#fff', mb: 2 }}>
        Contract Renewals
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Service</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Expires</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Days Left</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renewals.map((service) => (
              <TableRow key={service._id} hover>
                <TableCell>
                  <Chip
                    label={getUrgencyLabel(service.daysUntilExpiry, service.isOverdue)}
                    color={getUrgencyColor(service.daysUntilExpiry, service.isOverdue)}
                    size="small"
                    sx={{ fontWeight: 'bold', minWidth: '80px' }}
                  />
                </TableCell>
                <TableCell>
                  {service.client && ((service.client as any).companyName || (service.client as any).name) ? (
                    <Link
                      component="button"
                      variant="inherit"
                      onClick={() => handleClientClick((service.client as any)._id || service._id)}
                      sx={{ 
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 'bold',
                        color: '#fff',
                        fontSize: '0.875rem',
                        '&:hover': {
                          textDecoration: 'none',
                          color: '#e0e0e0'
                        }
                      }}
                    >
                      {(service.client as any).companyName || (service.client as any).name}
                    </Link>
                  ) : (
                    <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>Unknown Client</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 'bold' }}>
                    {service.serviceType}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {typeof service.supplier === 'object' && service.supplier !== null && 'name' in service.supplier
                      ? (service.supplier as any).name
                      : typeof service.supplier === 'string'
                      ? service.supplier
                      : 'Unknown Supplier'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#fff', fontSize: '0.875rem' }}>
                    {service.endDate ? format(service.endDate, 'MMM dd, yyyy') : 'No end date'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    sx={{ 
                      color: service.isOverdue ? '#f44336' : '#fff', 
                      fontSize: '0.875rem',
                      fontWeight: service.daysUntilExpiry !== null && service.daysUntilExpiry <= 30 ? 'bold' : 'normal'
                    }}
                  >
                    {service.isOverdue 
                      ? `${Math.abs(service.daysUntilExpiry || 0)} days overdue`
                      : service.daysUntilExpiry !== null 
                        ? `${service.daysUntilExpiry} days`
                        : 'Unknown'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export { ContractRenewalTimeline };
